import { useEffect, useState } from 'react'

// Diwali 2026: Sunday 8 November is fully booked. This announcement bar
// auto-clears on 9 November — on/after that date it renders nothing, so no
// code change is needed to take it down.
const BANNER_EXPIRY = new Date('2026-11-09T00:00:00')
const DISMISS_KEY = 'diwali-2026-dismissed'

const Star = ({ className }) => (
  <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M12 2l1.6 6.2c.2.7.8 1.3 1.5 1.5L21 11l-5.9 1.3c-.7.2-1.3.8-1.5 1.5L12 20l-1.6-6.2c-.2-.7-.8-1.3-1.5-1.5L3 11l5.9-1.3c.7-.2 1.3-.8 1.5-1.5L12 2z" />
  </svg>
)

// Clay oil lamp with a flickering flame.
const Diya = ({ className, delay = '0s' }) => (
  <svg aria-hidden="true" viewBox="0 0 32 28" className={className}>
    <circle cx="16" cy="10" r="8" fill="#FDE68A" opacity="0.3" />
    <path
      className="animate-flicker"
      style={{ animationDelay: delay }}
      d="M16 1C19.5 6 20.5 9.5 16 14C11.5 9.5 12.5 6 16 1Z"
      fill="#FBBF24"
    />
    <path d="M16 6C17.5 8.5 17.5 10.5 16 12.5C14.5 10.5 14.5 8.5 16 6Z" fill="#FFF7D6" />
    <path d="M2 15Q16 12 30 15Q28 26 16 26Q4 26 2 15Z" fill="#C2410C" />
    <path d="M2 15Q16 12 30 15" stroke="#FCD34D" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    <circle cx="10" cy="20" r="1.2" fill="#FCD34D" />
    <circle cx="16" cy="21.5" r="1.2" fill="#FCD34D" />
    <circle cx="22" cy="20" r="1.2" fill="#FCD34D" />
  </svg>
)

// Repeating phrases for the scrolling marquee strip.
const MARQUEE_ITEMS = [
  'HAPPY DIWALI',
  'SUNDAY 8 NOVEMBER IS FULLY BOOKED',
  'ORDERS OPEN FOR OTHER DAYS',
  'WISHING YOU LIGHT, LOVE & SWEETNESS',
]

function DiwaliBanner() {
  const [render, setRender] = useState(false)
  const [open, setOpen] = useState(false)

  useEffect(() => {
    const expired = new Date() >= BANNER_EXPIRY
    const dismissed = sessionStorage.getItem(DISMISS_KEY) === 'true'
    if (expired || dismissed) return

    setRender(true)
    // Trigger the slide-in on the next frame so the transition runs.
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [])

  if (!render) return null

  const dismiss = () => {
    setOpen(false)
    sessionStorage.setItem(DISMISS_KEY, 'true')
    window.setTimeout(() => setRender(false), 400)
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className={`relative overflow-hidden border-b-2 border-amber-300/60 text-white shadow-coral transform transition-all duration-500 ease-out ${
        open ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
      }`}
    >
      {/* Animated moving night-sky gradient */}
      <div
        aria-hidden="true"
        className="animate-gradient-x absolute inset-0 bg-gradient-to-r from-[#2A0A3D] via-[#7A1F5C] to-[#2A0A3D]"
      />
      {/* Warm lamp-glow blobs */}
      <div aria-hidden="true" className="absolute -top-10 left-1/4 h-24 w-24 rounded-full bg-amber-400/25 blur-2xl" />
      <div aria-hidden="true" className="absolute -bottom-10 right-1/4 h-24 w-24 rounded-full bg-orange-500/25 blur-2xl" />
      {/* Light sweep */}
      <span aria-hidden="true" className="banner-shimmer" />

      {/* Marigold garland (toran) along the top edge */}
      <svg aria-hidden="true" className="absolute inset-x-0 top-0 h-3 w-full">
        <defs>
          <pattern id="diwali-toran" width="32" height="12" patternUnits="userSpaceOnUse">
            <circle cx="3" cy="3" r="3" fill="#F97316" />
            <circle cx="9" cy="7" r="3" fill="#FBBF24" />
            <circle cx="16" cy="9" r="3" fill="#F97316" />
            <circle cx="23" cy="7" r="3" fill="#FBBF24" />
            <circle cx="29" cy="3" r="3" fill="#F97316" />
          </pattern>
        </defs>
        <rect width="100%" height="12" fill="url(#diwali-toran)" />
      </svg>

      {/* Twinkling sparkles */}
      <Star className="animate-twinkle absolute left-[6%] top-4 h-3 w-3 text-amber-200" />
      <Star className="animate-twinkle absolute left-[18%] bottom-10 h-2.5 w-2.5 text-white [animation-delay:0.6s]" />
      <Star className="animate-twinkle absolute right-[22%] top-5 h-2.5 w-2.5 text-amber-200 [animation-delay:1.1s]" />
      <Star className="animate-twinkle absolute right-[8%] bottom-10 h-3.5 w-3.5 text-white [animation-delay:0.3s]" />
      <Star className="animate-twinkle absolute left-1/2 top-3.5 hidden h-2.5 w-2.5 text-amber-200 sm:block [animation-delay:1.5s]" />

      <div className="container relative mx-auto px-4">
        <div className="relative flex items-center justify-center gap-3 pb-3 pr-10 pt-5 sm:gap-5 sm:pb-4 sm:pt-6">
          {/* Swinging sky lantern (akash kandil) */}
          <svg
            aria-hidden="true"
            viewBox="0 0 48 64"
            className="animate-swing h-12 w-9 shrink-0 drop-shadow-md sm:h-16 sm:w-12"
          >
            <path d="M24 0V12" stroke="#FCD34D" strokeWidth="1.5" />
            <rect x="18" y="10" width="12" height="4" rx="1" fill="#FCD34D" />
            <path d="M24 14L40 26H8Z" fill="#EA580C" stroke="#FCD34D" strokeWidth="1.2" strokeLinejoin="round" />
            <rect x="8" y="26" width="32" height="8" fill="#DB2777" stroke="#FCD34D" strokeWidth="1.2" />
            <path d="M8 34H40L24 46Z" fill="#EA580C" stroke="#FCD34D" strokeWidth="1.2" strokeLinejoin="round" />
            <circle className="animate-flicker" cx="24" cy="30" r="3.5" fill="#FEF3C7" />
            <path d="M24 46V60M20 45L17 58M28 45L31 58" stroke="#FCD34D" strokeWidth="1.2" strokeLinecap="round" />
          </svg>

          <p className="text-center leading-tight">
            <span className="flex items-center justify-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.25em] text-amber-200 sm:text-xs">
              <Diya className="h-4 w-5" />
              Happy Diwali
              <Diya className="h-4 w-5" delay="0.4s" />
            </span>
            <span className="mt-1 block bg-gradient-to-r from-amber-200 via-yellow-50 to-amber-200 bg-clip-text font-heading text-lg font-bold tracking-wide text-transparent text-balance drop-shadow-sm sm:text-2xl">
              Sunday 8 November is fully booked
            </span>
            <span className="mt-1 flex items-center justify-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-amber-100/90 sm:text-sm">
              <span className="h-px w-4 bg-amber-200/60 sm:w-6" />
              Please pick another day
              <span className="h-px w-4 bg-amber-200/60 sm:w-6" />
            </span>
          </p>

          {/* Row of diyas on wider screens */}
          <div aria-hidden="true" className="hidden shrink-0 items-end gap-1 md:flex">
            <Diya className="h-7 w-8" delay="0.2s" />
            <Diya className="h-9 w-10" delay="0.7s" />
            <Diya className="h-7 w-8" delay="1.1s" />
          </div>

          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss announcement"
            className="absolute right-0 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/20 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </div>

      {/* Scrolling festive marquee strip */}
      <div className="relative overflow-hidden border-t border-amber-200/25 bg-black/25 py-1.5">
        <div className="banner-marquee">
          {[0, 1].map((dup) => (
            <div key={dup} aria-hidden={dup === 1} className="flex shrink-0 items-center">
              {MARQUEE_ITEMS.map((item) => (
                <span key={item} className="flex items-center text-[11px] font-bold uppercase tracking-[0.2em] text-amber-100 sm:text-xs">
                  <Star className="mx-3 h-3 w-3 text-amber-300 sm:mx-4" />
                  {item}
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default DiwaliBanner
